import { useState } from 'react'
import {
  Typography,
  Card,
  CardContent,
  Box,
  Tooltip,
  Grid,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PropTypes from 'prop-types'
import { useFetch } from '../../hooks'
import { authenticatedFetch } from '../../services/api/apiClient'
import { auth0Claims } from '../../auth/consts'
import { apiEndpoints } from '../../consts'
import { LoadingSpinner } from '../../components/atoms'
import styles from './SettingsIntegrations.module.scss'

const integrationStatuses = {
  active: {
    color: 'primary',
    label: 'Active',
    icon: 'check-circle',
    description: 'Integration is setup and working'
  },
  warning: {
    color: 'secondary',
    label: 'Warning',
    icon: 'exclamation-triangle',
    description: 'Integration is setup but disabled (make sure its enabled on the customization page)'
  },
  error: {
    color: 'default',
    label: 'Error',
    icon: 'times-circle',
    description: 'This integration is setup but not working'
  },
  inactive: {
    color: 'default',
    label: 'Click to Enable',
    icon: 'plus-circle',
    description: 'This integration is not setup for this client'
  }
}

const integrations = [
  {
    id: 'lawmatics',
    name: 'Lawmatics',
    description: 'Automatically send call data and lead information to Lawmatics',
    logo: 'https://www.lawmatics.com/wp-content/uploads/2025/02/Lawmatics-logo_white-bg_og.png',
    configPath: '/integrations/lawmatics'
  }
]

const SettingsIntegrations = ({ user }) => {
  const [logoErrors, setLogoErrors] = useState({})

  const getUserCampaign = () => {
    return user?.[auth0Claims.userAuthClaim]?.user_metadata?.Organization || null
  }

  const campaignName = getUserCampaign()

  const { data: integrationStates, loading } = useFetch(
    apiEndpoints.integrationsCampaign(campaignName),
    {
      initialData: {},
      skip: !campaignName,
      errorMessage: 'Failed to fetch integration states'
    }
  )

  const getIntegrationStatus = (integrationId) => {
    const state = integrationStates[integrationId]

    if (!state) return 'inactive'

    if (state.hasToken && state.enabled) {
      return state.hasIssues ? 'warning' : 'active'
    }

    if (state.hasToken && !state.enabled) {
      return 'warning'
    }

    return 'inactive'
  }

  const handleIntegrationClick = async (integration) => {
    if (integration.id === 'lawmatics') {
      const baseUrl = process.env.REACT_APP_LAWMATICS_REDIRECT_URL

      const campaignName = getUserCampaign()

      if (campaignName) {
        try {
          const campaignsResponse = await authenticatedFetch(apiEndpoints.campaigns)
          if (campaignsResponse.ok) {
            await campaignsResponse.json()

            const response = await authenticatedFetch(apiEndpoints.campaignsByName, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ campaignName })
            })

            if (response.ok) {
              const { campaignId } = await response.json()

              const urlWithParams = `${baseUrl}?campaignId=${campaignId}&clientName=${encodeURIComponent(campaignName)}`
              window.open(urlWithParams, '_blank')
            } else {
              window.open(baseUrl, '_blank')
            }
          } else {
            window.open(baseUrl, '_blank')
          }
        } catch (error) {
          window.open(baseUrl, '_blank')
        }
      } else {
        window.open(baseUrl, '_blank')
      }
    }
  }

  if (loading) {
    return (
      <LoadingSpinner />
    )
  }

  return (
    <div className={styles.root}>
      <Box className={styles.pageHeader}>
        <Typography variant='h4' component='h1' className={styles.pageTitle}>
          Integrations
        </Typography>
        <Typography className={styles.pageSubtitle}>
          Connect and manage third-party integrations
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {integrations.map((integration) => {
          const status = getIntegrationStatus(integration.id)
          const statusConfig = integrationStatuses[status]

          return (
            <Grid item xs={12} md={integrations.length === 1 ? 12 : 6} key={integration.id}>
              <Box className={styles.integrationWrapper}>
                <Card
                  className={styles.integrationCard}
                  role='button'
                  tabIndex={0}
                  aria-label={`Configure ${integration.name} integration`}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleIntegrationClick(integration)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleIntegrationClick(integration)
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <CardContent className={styles.cardContent}>
                    <Box display='flex' alignItems='center' justifyContent='center' mb={3}>
                      {integration.logo && !logoErrors[integration.id] ? (
                        <img
                          src={integration.logo}
                          alt={integration.name}
                          className={styles.integrationLogo}
                          onError={() => {
                            setLogoErrors(prev => ({ ...prev, [integration.id]: true }))
                          }}
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon='plug'
                          className={styles.integrationIcon}
                          aria-hidden='true'
                        />
                      )}
                    </Box>

                    <Typography variant='h5' component='h3' className={styles.integrationTitle}>
                      {integration.name}
                    </Typography>

                    <Typography variant='body1' color='textSecondary' className={styles.integrationDescription}>
                      {integration.description}
                    </Typography>
                  </CardContent>
                </Card>

                <Tooltip title={statusConfig.description} arrow>
                  <Box
                    role='button'
                    tabIndex={0}
                    aria-label={`${integration.name} status: ${statusConfig.label}`}
                    className={`${styles.statusBubble} ${styles[`status-${status}`]}`}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleIntegrationClick(integration)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleIntegrationClick(integration)
                      }
                    }}
                  >
                    <FontAwesomeIcon
                      icon={statusConfig.icon}
                      className={styles.statusIcon}
                    />
                    <span className={styles.statusLabel}>{statusConfig.label}</span>
                  </Box>
                </Tooltip>
              </Box>
            </Grid>
          )
        })}
      </Grid>
    </div>
  )
}

SettingsIntegrations.propTypes = {
  user: PropTypes.object
}

export default SettingsIntegrations
